import { useContext, useState } from 'react';
import {
  InfoCard,
  IDateRange,
  ModalContext,
  FormProvider,
  PaginationProvider,
  useCheckIfSubscriptionFeatureActiveBroker,
  getPeriodRangeByPeriodType,
  DatePeriodTypesEnum,
} from 'dash-ui';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import Construction from 'assets/images/png/Construction.png';
import AddAlertModal from 'containers/alerts/AddAlertModal/AddAlertModal';
import { FormFieldsList } from 'containers/alerts/AddAlertModal/constants/constants';
import {
  useCurrentBrokerHasAlertConfigsQuery,
  useGetNumberOfNewAlertItemsLazyQuery,
  useObserveBrokerAlertItemChangesSubscription,
} from 'gql/generated.graphql';

import { PropertiesAlertsModal } from './components';

function PropertyAlertsWidget() {
  const theme = useTheme();
  const { t: tCommon } = useTranslation('common', { keyPrefix: 'labels' });
  const { t } = useTranslation('pages', {
    keyPrefix: 'dashboardPage.propertyAlertsWidget',
  });
  const [countOfNewAlerts, setCountOfNewAlerts] = useState<number>(0);
  const [range, setRange] = useState<IDateRange>(
    getPeriodRangeByPeriodType(DatePeriodTypesEnum.CURRENT_WEEK)
  );

  const { toggleModal, setPayload } = useContext(ModalContext);
  const { data, loading: hasConfigsCheckLoading } =
    useCurrentBrokerHasAlertConfigsQuery({ fetchPolicy: 'network-only' });
  const { currentBrokerHasAlertConfigs = false } = data || {};
  const [lazyGetNumberOfNewAlertItems, { refetch, loading: infoLoading }] =
    useGetNumberOfNewAlertItemsLazyQuery();
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();

  useObserveBrokerAlertItemChangesSubscription({
    onData() {
      refetch();
    },
  });

  return (
    <FormProvider fieldsList={FormFieldsList}>
      <AddAlertModal />
      <PaginationProvider>
        <PropertiesAlertsModal />
      </PaginationProvider>
      <InfoCard
        range={range}
        loading={hasConfigsCheckLoading || infoLoading}
        hasInfoData={currentBrokerHasAlertConfigs}
        infoDescription={t('infoDescription')}
        infoPlaceholderDescription={t('infoPlaceholderDescription')}
        infoPlaceholder={{
          iconName: 'notification',
          info: t('infoPlaceholder') || '',
        }}
        bgColor={theme.colors.g20}
        buttonTitle={tCommon(currentBrokerHasAlertConfigs ? 'view' : 'track')}
        bgImgSrc={Construction}
        buttonDisabled={currentBrokerHasAlertConfigs && !countOfNewAlerts}
        onButtonClick={() => {
          if (!isSubscriptionActive) {
            return toggleModal(true, 'UpgradeSubscriptionModal');
          }
          if (currentBrokerHasAlertConfigs) {
            setPayload({ range });

            return toggleModal(true, 'PropertiesAlertsModal');
          }

          return toggleModal(true, 'AddAlertModal');
        }}
        itemsInfo={[
          {
            info: countOfNewAlerts,
            iconName: 'notification',
            key: 'notification',
          },
        ]}
        onRangeChange={async (dateRange) => {
          setRange(dateRange);

          const { data } = await lazyGetNumberOfNewAlertItems({
            variables: { range: dateRange },
          });
          const { getNumberOfNewAlertItems = 0 } = data || {};

          setCountOfNewAlerts(getNumberOfNewAlertItems);
        }}
      />
    </FormProvider>
  );
}

export default PropertyAlertsWidget;
